import { Card, Table, Row, Typography, Button, Spin, message, Col, Switch, Tooltip, Modal, Popconfirm } from "antd";
import { useEffect, useState } from "react";
import { useMediaQuery } from "react-responsive";
import HTTPClient from "../../utils/HTTPClient";
import { useNavigate, useSearchParams } from "react-router-dom";
import moment from "moment/moment";
import { CalendarOutlined, DeleteOutlined, PlusCircleOutlined, EditOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import { CreateModal } from "./CreateModal";

const AB_Flow = () => {
    const navigate = useNavigate();
    const [flowData, setFlowData] = useState(null);
    const [loading, setLoading] = useState(false);
    const [showRunModal, setShowRunModal] = useState(false);
    const [runData, setRunData] = useState(null);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const { t } = useTranslation();

    useEffect(() => {
        const fetch = async () => {
            setLoading(true);
            let res = null;
            try {
                res = await HTTPClient.get("/ab_flow/");
            } catch (e) {
                if (e.response.status === 404) {
                    await HTTPClient.post("/ab_flow/start_ab_flow");
                    res = await HTTPClient.get("/ab_flow/");
                }
                if (e.response.status === 402) {
                    navigate("/home");
                }
            }
            if (res) {
                const flowData = res.data;
                setFlowData(flowData);
            }
            setLoading(false);
        };
        fetch();
    }, []);

    const toggleStatusFlow = async (selectedFlow) => {
        setLoading(true);
        await HTTPClient.put(`/ab_flow/flow/${selectedFlow.id}/status`, { status: !selectedFlow.status });
        const res = await HTTPClient.get("/ab_flow/");
        const newFlowData = res.data;
        setFlowData(newFlowData);
        setLoading(false);
    }

    const get_edit_url = async (flow_id) => {
        setLoading(true);
        const response = await HTTPClient.get(`/ab_flow/edit_url/${flow_id}`);
        window.open(response.data, "_blank", "noopener,noreferrer");
        setLoading(false);
    };

    const update_name = async (flow_id, new_name) => {
        setLoading(true);
        await HTTPClient.put(`/ab_flow/flow/${flow_id}/name`, { name: new_name });
        const res = await HTTPClient.get("/ab_flow/");
        const newFlowData = res.data;
        setFlowData(newFlowData);
        setLoading(false);
    };

    const delete_flow = async (flow_id) => {
        setLoading(true);
        await HTTPClient.delete(`/ab_flow/flow/${flow_id}`);
        const res = await HTTPClient.get("/ab_flow/");
        const newFlowData = res.data;
        setFlowData(newFlowData);
        setLoading(false);
    };

    const getRuns = async (flow_id) => {
        HTTPClient.get(`/ab_flow/flow-runs/${flow_id}`).then((response) => {
            setRunData(response.data);
            setShowRunModal(true);
        });
    };

    const isLargeScreen = useMediaQuery({
        query: "(min-width: 771px)",
    });

    let dataColumns = [
        {
            title: t("ab_flow.list.name"),
            dataIndex: "name",
            key: "name",
            showMobile: true,
            render: (name, record) => (
                <Typography.Text
                    editable={{
                        onChange: (newName) => {
                            if (newName !== name && newName !== "") {
                                update_name(record.id, newName);
                            }
                        },
                        tooltip: t("ab_flow.list.edit_name"),
                        maxLength: 50,
                        autoSize: { maxRows: 1 }
                    }}
                >
                    {name || "Flow"}
                </Typography.Text>
            )
        },
        {
            title: t("ab_flow.list.creation_date"),
            dataIndex: "creation_date",
            key: "creation_date",
            showMobile: false,
            render: (datetime) => moment(datetime).format("DD/MM/YYYY HH:mm")
        },
        {
            title: t("ab_flow.list.status"),
            key: "status",
            showMobile: false,
            render: (flow) => <Switch checked={flow.status} onChange={() => toggleStatusFlow(flow)} />,
        },
        {
            title: t("ab_flow.list.edit"),
            key: "edit",
            showMobile: true,
            render: (flow) => <Row style={{ display: "flex", justifyContent: "center" }}><Button type="primary" onClick={() => get_edit_url(flow.id)}><EditOutlined /></Button></Row>
        },
        {
            title: t("ab_flow.list.delete"),
            key: "delete",
            showMobile: true,
            render: (flow) => (
                <Row style={{ display: "flex", justifyContent: "center" }}>
                    <Popconfirm
                        title={t("ab_flow.list.delete_confirm.title")}
                        description={t("ab_flow.list.delete_confirm.description")}
                        onConfirm={() => delete_flow(flow.id)}
                        okText={t("ab_flow.list.delete_confirm.ok")}
                        cancelText="No"
                        placement="left"
                    >
                        <Button
                            danger
                            type="primary"
                        >
                            <DeleteOutlined />
                        </Button>
                    </Popconfirm>
                </Row>
            )
        }
    ];

    if (!isLargeScreen) {
        dataColumns = dataColumns.filter((col) => col.showMobile);
    }

    return (<>
        <Row style={{ backgroundColor: "white", width: "100%", height: isLargeScreen ? "134px" : "180px", padding: "27px 34px" }}>
            <Row>
                <Typography.Text style={{ fontSize: "27px", fontWeight: "bold" }}>
                    {t("ab_flow.title")}
                </Typography.Text>
            </Row>
            <Row style={{ width: "100%", marginTop: "10px" }}>
                <Typography.Text style={{ fontSize: "20px" }}>{t("ab_flow.subtitle")}</Typography.Text>
            </Row>
        </Row>
        <Row style={{ padding: isLargeScreen ? "27px 34px" : "10px 10px" }}>

            {loading || !flowData ? (
                <div
                    style={{
                        width: "100%",
                        minHeight: "20vh",
                        overflow: "auto",
                        display: "flex",
                        justifyContent: "center",
                        alignItems: "center",
                    }}>
                    <Spin />
                </div>
            ) : (
                <>
                    <Row style={{ width: "100%" }}>
                        <Col span={isLargeScreen ? 17 : 24}>
                            <div style={{ boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)", backgroundColor: "white", width: "100%", height: "100%", padding: "22px 27px", borderRadius: "14px" }}>
                                <Row style={{ display: "flex", justifyContent: "space-between" }}>
                                    <Col>
                                        <Typography.Text style={{ fontSize: "18px", fontWeight: "bold" }}>{t("ab_flow.list.title")}</Typography.Text><br />
                                        <Typography.Text style={{ fontSize: "12px" }}>{t("ab_flow.list.subtitle")}</Typography.Text>
                                    </Col>
                                </Row>

                                <Row>
                                    <Table
                                        columns={dataColumns}
                                        dataSource={flowData?.flows}
                                        pagination={true}
                                        style={{ marginTop: "25px", width: "100%" }}
                                    />
                                </Row>
                            </div>
                        </Col>
                        {isLargeScreen && <Col span={1}></Col>}
                        <Col span={isLargeScreen ? 6 : 24} style={{ display: "flex", justifyContent: "space-between", flexDirection: "column" }}>
                            <Row style={{ marginTop: isLargeScreen ? "0px" : "50px", marginBottom: isLargeScreen ? "0px" : "50px", boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)", backgroundColor: "white", width: "100%", height: "130px", padding: "18px 22px", borderRadius: "14px" }}>
                                <Col span={16} style={{ display: "flex", justifyContent: "center", flexDirection: "column" }}>
                                    <Row><Typography.Text style={{ fontSize: "18px", fontWeight: "bold" }}>{t("ab_flow.usage.title")}</Typography.Text><Tooltip title={t("ab_flow.usage.tooltip")}><InfoCircleOutlined style={{ marginLeft: "5px" }} /></Tooltip></Row>
                                </Col>
                                <Col span={8} style={{ display: "flex", justifyContent: "center", flexDirection: "column", alignItems: "center" }}>
                                    <Row><Typography.Text style={{ fontSize: "24px", fontWeight: "bold" }}>{flowData?.usage}/{flowData?.limit}</Typography.Text></Row>
                                    {/*<Row><RocketOutlined /><Typography.Text style={{ fontSize: "12px", fontWeight: "bold" }}>{t("ab_flow.usage.upgrade")}</Typography.Text></Row>*/}
                                </Col>
                            </Row>
                            <Row style={{ boxShadow: "0px 4px 4px rgba(0, 0, 0, 0.25)", backgroundColor: "white", width: "100%", height: "50%", padding: "22px 27px", borderRadius: "14px" }}>
                                <Row style={{ width: "100%" }}>
                                    <Typography.Text style={{ fontSize: "18px", fontWeight: "bold" }}>{t("ab_flow.create.title")}</Typography.Text>
                                </Row>
                                <Row style={{ width: "100%", height: "50%", display: "flex", justifyContent: "center", alignItems: "center" }}>
                                    <Button onClick={() => setShowCreateModal(true)} icon={<PlusCircleOutlined style={{ fontSize: "150%" }} />} type="primary" style={{ width: "100%", height: "100%" }}><Typography.Text style={{ fontSize: "24px", color: "white" }}>{t("ab_flow.create.button_creation")}</Typography.Text></Button>
                                </Row>
                            </Row>

                        </Col>
                    </Row>

                </>
            )}
        </Row>
        <CreateModal
            open={showCreateModal}
            onCancel={() => setShowCreateModal(false)}
            setLoading={setLoading}
            setFlowData={setFlowData}
            setShowCreateModal={setShowCreateModal}
        />
        <Modal
            title={t("ab_flow.runs.title")}
            open={showRunModal}
            onCancel={() => setShowRunModal(false)}
            footer={null}
            width={800}
        >
            <Table
                columns={[
                    {
                        title: t("ab_flow.runs.date"),
                        dataIndex: "startTime",
                        key: "startTime",
                        render: (date) => moment(date).format("DD/MM/YYYY HH:mm")
                    },
                    {
                        title: t("ab_flow.runs.status"),
                        dataIndex: "status",
                        key: "status",
                        render: (status) => <Typography.Text style={{ color: status === "SUCCEEDED" ? "green" : "red" }}>{status}</Typography.Text>
                    }
                ]}
                dataSource={runData}
                pagination={{ pagesize: 5 }}
            />
        </Modal>
    </>
    );
};

export { AB_Flow };
